import useCantabs from '../hooks/useCantabs';
import { bool } from 'prop-types';
import ListApp from './ListApp';
import CanTabHeader from './CanTabHeader';
import SidebarErrorBoundary from './SidebarErrorBoundary';
import { MuiList } from 'components/mui';
import styles from '../styles/cantab.module.css';
import { Drawer, List } from '@mui/material';
import { useEffect } from 'react';
import { getCanTabState, ensureLegacyCanTabState } from '../libs/useLocalStorage';
import { useState } from 'react';
import { useSidebarAlertStatus } from 'hooks/sense/useSidebarAlertStatus';
import { useSelector } from 'react-redux';
import { selectUserInfo } from 'redux/userInfo/userInfoSlice';

/**
 * @component CanTab
 * @returns {JSX.Element}
 */
function CanTab({ open, onOpen, onClose, isMd }) {
	const { menus, user } = useCantabs();
	const [activeTab, setActiveTab] = useState('');
	const { userInfo } = useSelector(selectUserInfo);

	// Check if user has access to CANsense/alerts
	// Only allow specific CANsense roles: CANsense_Admin, CANsense_Client, or CANsense_View
	const hasAlertAccess = userInfo?.userRoleGroups?.some(
		role => {
			const roleName = role.name || '';
			return roleName === 'CANsense_Admin' ||
			       roleName === 'CANsense_Client' ||
			       roleName === 'CANsense_View';
		}
	) || false;

	// Get lightweight alert status for sidebar (only fetches if user has access)
	// This hook is safe - failures don't crash the app, just returns empty state
	const alertStatus = useSidebarAlertStatus(hasAlertAccess);

	// Transform to match the format expected by ListApp
	const alertsData = hasAlertAccess ? {
		alertStatus: alertStatus.error ? null : {
			alerts: {
				total: alertStatus.activeAlertCount,
				new: alertStatus.newAlertCount,
				acknowledged: alertStatus.acknowledgedCount,
				resolved: 0,
				highestSeverity: alertStatus.highestSeverity,
			},
		},
		hasActiveAlerts: alertStatus.activeAlertCount > 0,
		activeAlertCount: alertStatus.activeAlertCount,
		newAlertCount: alertStatus.newAlertCount,
		acknowledgedCount: alertStatus.acknowledgedCount,
		resolvedCount: 0,
		highestSeverity: alertStatus.highestSeverity,
		isConnected: alertStatus.isConnected,
		connectionState: alertStatus.isConnected ? 'connected' : 'disconnected',
		loading: alertStatus.loading,
		error: alertStatus.error,
	} : null;

	// Seed the legacy CANtabs localStorage shape so clients that still hit the
	// old sidebar on other pages don't crash on a missing `openTabs` key.
	useEffect(() => {
		ensureLegacyCanTabState();
	}, []);

	useEffect(() => {
		if (!isMd) {
			const canTabSettings = getCanTabState();
			if (canTabSettings.menuState === 'open') {
				onOpen();
			} else {
				onClose();
			}
		}
		setActiveTab(window.location.pathname + window.location.search);
	}, [onClose, onOpen, isMd]);

	return (
		<>
			{isMd ? (
				<Drawer
					open={open}
					onClose={onClose}
					anchor="left"
					PaperProps={{
						className: styles.paper,
					}}
				>
					<List disablePadding data-tour="nav-sidebar">
						<CanTabHeader
							open={open}
							user={user}
							onClick={open ? onClose : onOpen}
						/>
						<MuiList>
							{menus.map((menu) => (
								<SidebarErrorBoundary key={menu.id} sectionName={menu.name || 'Menu'}>
									<ListApp
										menu={menu}
										menuOpen={open}
										setMenuOpen={onOpen}
										activeTab={activeTab}
										alertsData={alertsData}
									/>
								</SidebarErrorBoundary>
							))}
						</MuiList>
					</List>
				</Drawer>
			) : (
				<section
					className={`${open ? styles.expand : styles.unexpand}`}
					data-tour="nav-sidebar"
				>
					<CanTabHeader
						open={open}
						user={user}
						onClick={open ? onClose : onOpen}
					/>
					<MuiList>
						{menus.map((menu) => (
							<SidebarErrorBoundary key={menu.id} sectionName={menu.name || 'Menu'}>
								<ListApp
									menu={menu}
									menuOpen={open}
									setMenuOpen={onOpen}
									setMenuClose={onClose}
									activeTab={activeTab}
									alertsData={alertsData}
								/>
							</SidebarErrorBoundary>
						))}
					</MuiList>
				</section>
			)}
		</>
	);
}

export default CanTab;
